import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Minus, Trash2, ShoppingCart, CreditCard, DollarSign, GraduationCap, Calendar as CalIcon, CheckCircle2, AlertTriangle, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';

export default function POS() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [q, setQ] = useState('');
  const [customer, setCustomer] = useState({ name: '', email: '' });
  const [linkedClient, setLinkedClient] = useState(null); // selected client object
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [method, setMethod] = useState('stripe');
  const [bntOpen, setBntOpen] = useState(null);

  useEffect(() => { api.get('/pos/products').then(r => setProducts(r.data)); }, []);

  // Auto-load client from URL param
  useEffect(() => {
    const cid = params.get('client_id');
    if (cid) {
      api.get(`/admin/clients/${cid}`).then(r => {
        const c = r.data.client;
        setLinkedClient(c);
        setCustomer({ name: c.full_name, email: c.email });
        toast.success(`POS pre-filled for ${c.full_name}`);
      }).catch(()=>{});
    }
  }, [params]);

  const loadClients = async () => {
    const { data } = await api.get('/admin/clients');
    setAllClients(data);
  };

  const pickClient = (c) => {
    setLinkedClient(c);
    setCustomer({ name: c.full_name, email: c.email });
    setClientPickerOpen(false);
    setParams({ client_id: c.id });
  };

  const clearClient = () => {
    setLinkedClient(null);
    setCustomer({ name: '', email: '' });
    setParams({});
  };

  const clientFiltered = allClients.filter(c => !clientSearch || [c.full_name, c.email, c.phone].some(x => (x||'').toLowerCase().includes(clientSearch.toLowerCase())));

  const filtered = products.filter(p => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.category||'').toLowerCase().includes(q.toLowerCase()));
  const retail = filtered.filter(p => !p.is_training);
  const training = filtered.filter(p => p.is_training);
  const cats = useMemo(() => Array.from(new Set(retail.map(p => p.category || 'Other'))), [retail]);

  const add = (p) => setCart(c => { const ex = c.find(x => x.product.id === p.id); return ex ? c.map(x => x.product.id === p.id ? {...x, qty: x.qty+1} : x) : [...c, { product: p, qty: 1 }]; });
  const dec = (id) => setCart(c => c.map(x => x.product.id === id ? {...x, qty: Math.max(1, x.qty-1)} : x));
  const inc = (id) => setCart(c => c.map(x => x.product.id === id ? {...x, qty: x.qty+1} : x));
  const remove = (id) => setCart(c => c.filter(x => x.product.id !== id));
  const subtotal = useMemo(() => cart.reduce((s, x) => s + x.product.price * x.qty, 0), [cart]);
  const tax = useMemo(() => Math.round(subtotal * 0.0825 * 100) / 100, [subtotal]);
  const total = useMemo(() => Math.round((subtotal + tax) * 100) / 100, [subtotal, tax]);

  const checkout = async () => {
    if (cart.length === 0) return;
    try {
      const payload = {
        items: cart.map(x => ({ product_id: x.product.id, quantity: x.qty })),
        customer_email: customer.email || null, customer_name: customer.name || null,
        client_id: linkedClient?.id || null,
        payment_method: method, origin_url: window.location.origin,
      };
      const { data } = await api.post('/pos/orders', payload);
      if (method === 'stripe' && data.checkout_url) { window.location.href = data.checkout_url; return; }
      if (method === 'cash') {
        toast.success(`Order #${data.order.id.substring(0,8)} — $${total} (cash)${linkedClient ? ` · ${linkedClient.full_name}` : ''}`);
        setCart([]); return;
      }
      if (method === 'paypal') toast.error('PayPal POS coming soon — use Stripe or cash');
    } catch (err) { toast.error(err.response?.data?.detail || 'Checkout failed'); }
  };

  return (
    <div className='p-6 lg:p-8'>
      <div className='flex items-center justify-between mb-4'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Native POS</div>
          <h1 className='font-display text-3xl font-bold text-slate-900 mt-1'>JFK9 Terminal</h1>
          <p className='text-slate-500 text-sm mt-1'>Retail on Stripe rail + Board & Train scheduling with trainer conflict checks.</p>
        </div>
      </div>

      {/* Board & Train section */}
      {training.length > 0 && (
        <div className='mb-6'>
          <div className='flex items-center gap-2 mb-3'>
            <GraduationCap className='w-5 h-5 text-[#C8102E]' />
            <h2 className='font-display text-lg font-bold text-slate-900'>Board & Train Programs</h2>
            <Badge className='bg-[#C8102E]/20 text-[#FCA5A5] border-0'>Schedules a training booking</Badge>
          </div>
          <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-3'>
            {training.map(p => (
              <Card key={p.id} className='p-4 bg-gradient-to-br from-[#0F172A] to-[#1B3A87]/30 border-[#1B3A87]/60 hover:border-[#C8102E] cursor-pointer transition-all' onClick={()=>setBntOpen(p)} data-testid={`bnt-product-${p.id}`}>
                <div className='flex items-center justify-between'>
                  <div className='font-display font-bold text-slate-900'>{p.name}</div>
                  <GraduationCap className='w-5 h-5 text-[#C8102E]' />
                </div>
                <div className='text-xs text-slate-500 mt-1.5 line-clamp-3'>{p.description}</div>
                <div className='flex items-center justify-between mt-3'>
                  <div className='font-display font-bold text-2xl text-slate-900 tabular-nums'>${p.price.toFixed(0)}</div>
                  {p.duration_days > 0 ? <Badge className='bg-[#1B3A87] text-slate-900 border-0'>{p.duration_days} days</Badge> : <Badge className='bg-[#1B3A87] text-slate-900 border-0'>1 hour</Badge>}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className='grid lg:grid-cols-3 gap-4'>
        <div className='lg:col-span-2 space-y-3'>
          <Input placeholder='Search products…' value={q} onChange={(e)=>setQ(e.target.value)} className='bg-white border-slate-200 text-slate-900' data-testid='pos-search-input' />
          {cats.map(cat => (
            <div key={cat}>
              <div className='text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2'>{cat}</div>
              <div className='grid sm:grid-cols-2 md:grid-cols-3 gap-3'>
                {retail.filter(p => (p.category||'Other') === cat).map(p => (
                  <Card key={p.id} className='p-4 bg-white border-slate-200 hover:border-[#1B3A87] cursor-pointer transition-colors' onClick={()=>add(p)} data-testid={`pos-product-${p.id}`}>
                    <div className='font-semibold text-sm text-slate-900'>{p.name}</div>
                    <div className='text-[10px] text-slate-500 font-mono'>{p.sku}</div>
                    <div className='font-display font-bold text-xl text-slate-900 mt-2 tabular-nums'>${p.price.toFixed(2)}</div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        <Card className='p-4 bg-white border-slate-200 flex flex-col h-fit lg:sticky lg:top-4' data-testid='pos-cart'>
          <div className='font-display font-semibold text-lg mb-3 flex items-center gap-2'><ShoppingCart className='w-5 h-5' /> Cart ({cart.length})</div>
          {cart.length === 0 ? <div className='text-sm text-slate-500 py-4'>Empty. Tap a product to add.</div> : (
            <div className='space-y-2'>
              {cart.map(x => (
                <div key={x.product.id} className='flex items-center gap-2 text-sm' data-testid={`cart-item-${x.product.id}`}>
                  <div className='flex-1 truncate'>
                    <div className='font-semibold truncate'>{x.product.name}</div>
                    <div className='text-xs text-slate-500 tabular-nums'>${x.product.price.toFixed(2)} × {x.qty}</div>
                  </div>
                  <button className='w-7 h-7 rounded border border-slate-200 hover:bg-slate-50' onClick={()=>dec(x.product.id)}><Minus className='w-3 h-3 mx-auto' /></button>
                  <span className='font-mono text-sm w-6 text-center'>{x.qty}</span>
                  <button className='w-7 h-7 rounded border border-slate-200 hover:bg-slate-50' onClick={()=>inc(x.product.id)}><Plus className='w-3 h-3 mx-auto' /></button>
                  <button className='ml-1' onClick={()=>remove(x.product.id)}><Trash2 className='w-3 h-3 text-[#B42318]' /></button>
                </div>
              ))}
            </div>
          )}
          <div className='border-t border-slate-200 mt-3 pt-3 space-y-1 text-sm'>
            <div className='flex justify-between text-slate-500'><span>Subtotal</span><span className='tabular-nums'>${subtotal.toFixed(2)}</span></div>
            <div className='flex justify-between text-slate-500'><span>Tax (8.25%)</span><span className='tabular-nums'>${tax.toFixed(2)}</span></div>
            <div className='flex justify-between font-display font-bold text-lg pt-1'><span>Total</span><span className='tabular-nums'>${total.toFixed(2)}</span></div>
          </div>
          <div className='mt-3 space-y-2'>
            {linkedClient ? (
              <div className='flex items-center gap-2 p-2 rounded border border-[#1B3A87] bg-[#1B3A87]/15' data-testid='pos-linked-client'>
                <div className='w-8 h-8 rounded-full bg-[#1B3A87] text-slate-900 flex items-center justify-center font-bold text-xs'>{linkedClient.full_name[0]}</div>
                <div className='min-w-0 flex-1'>
                  <div className='text-sm font-semibold text-slate-900 truncate'>{linkedClient.full_name}</div>
                  <div className='text-[10px] text-slate-500 truncate'>{linkedClient.email}</div>
                </div>
                <button onClick={clearClient} className='text-slate-500 hover:text-slate-900' data-testid='pos-clear-client'><X className='w-4 h-4' /></button>
              </div>
            ) : (
              <Button type='button' variant='outline' className='w-full border-slate-200 text-slate-600 hover:bg-slate-50' onClick={() => { loadClients(); setClientPickerOpen(true); }} data-testid='pos-pick-client-button'>
                <UserPlus className='w-4 h-4 mr-2' /> Link to existing client
              </Button>
            )}
            <Input placeholder='Customer name (optional)' value={customer.name} onChange={(e)=>setCustomer({...customer, name: e.target.value})} className='bg-slate-50 border-slate-200 text-slate-900' data-testid='pos-customer-name' />
            <Input placeholder='Customer email (optional)' value={customer.email} onChange={(e)=>setCustomer({...customer, email: e.target.value})} className='bg-slate-50 border-slate-200 text-slate-900' data-testid='pos-customer-email' />
            <div className='grid grid-cols-3 gap-1.5'>
              {[{l:'Stripe',v:'stripe',tid:'pos-method-stripe'},{l:'PayPal',v:'paypal',tid:'pos-method-paypal'},{l:'Cash',v:'cash',tid:'pos-method-cash'}].map(m => (
                <button key={m.v} onClick={()=>setMethod(m.v)} className={`px-2 py-2 rounded border text-xs font-semibold ${method === m.v ? 'border-[#1B3A87] bg-[#1B3A87] text-slate-900' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid={m.tid}>{m.l}</button>
              ))}
            </div>
            <Button disabled={cart.length === 0} onClick={checkout} className='w-full bg-[#C8102E] hover:bg-[#A50D26] text-slate-900 h-11' data-testid='pos-checkout-button'>
              {method === 'cash' ? <DollarSign className='w-4 h-4 mr-2' /> : <CreditCard className='w-4 h-4 mr-2' />} Checkout
            </Button>
          </div>
        </Card>
      </div>

      <BntScheduler product={bntOpen} onClose={()=>setBntOpen(null)} />

      {/* Client picker dialog */}
      <Dialog open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
        <DialogContent className='max-w-lg max-h-[80vh] overflow-y-auto'>
          <DialogHeader><DialogTitle>Link to existing client</DialogTitle></DialogHeader>
          <Input placeholder='Search by name or email…' value={clientSearch} onChange={(e)=>setClientSearch(e.target.value)} data-testid='pos-client-search' autoFocus />
          <div className='space-y-1 mt-3 max-h-[50vh] overflow-y-auto'>
            {clientFiltered.map(c => (
              <button key={c.id} className='w-full text-left p-3 rounded border border-slate-200 hover:border-[#1B3A87] hover:bg-[#1B3A87]/5 transition-colors' onClick={()=>pickClient(c)} data-testid={`pos-client-pick-${c.id}`}>
                <div className='flex items-center gap-3'>
                  <div className='w-9 h-9 rounded-full bg-[#1B3A87] text-slate-900 flex items-center justify-center font-bold text-sm'>{c.full_name[0]}</div>
                  <div className='min-w-0 flex-1'>
                    <div className='font-semibold text-sm truncate'>{c.full_name}</div>
                    <div className='text-[11px] text-slate-500 truncate'>{c.email} · {c.dogs_count} dogs · {c.bookings_count} bookings</div>
                  </div>
                </div>
              </button>
            ))}
            {clientFiltered.length === 0 && <div className='text-sm text-slate-500 text-center py-6'>No clients match.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BntScheduler({ product, onClose }) {
  const [clients, setClients] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [runs, setRuns] = useState([]);
  const [trainerAvail, setTrainerAvail] = useState([]);
  const [form, setForm] = useState({
    client_id: '', dog_id: '', trainer_id: '', run_id: '',
    start: '', end: '',
    behavioral_concerns: '', bitten_history: '', medical_issues: '', notes: '',
    payment_method: 'cash',
  });
  const [submitting, setSubmitting] = useState(false);

  const isBnT = product?.program_type === '2week_bnt' || product?.program_type === '4week_bnt';
  const duration = product?.duration_days || 0;

  useEffect(() => {
    if (!product) return;
    api.get('/admin/clients').then(r => setClients(r.data));
    api.get('/trainers/list').then(r => setTrainers(r.data));
    api.get('/runs').then(r => setRuns(r.data));
    // Reset form
    setForm(f => ({...f, client_id: '', dog_id: '', trainer_id: '', run_id: '', start: '', end: ''}));
  }, [product]);

  // Compute end from start + duration
  useEffect(() => {
    if (!form.start) { setForm(f => ({...f, end: ''})); return; }
    const start = new Date(form.start);
    let end;
    if (duration > 0) { end = new Date(start); end.setDate(end.getDate() + duration); }
    else { end = new Date(start.getTime() + 60*60*1000); }
    setForm(f => ({...f, end: end.toISOString().slice(0,16)}));
  }, [form.start, duration]);

  // Refresh trainer availability when dates change
  useEffect(() => {
    if (!form.start || !form.end) { setTrainerAvail([]); return; }
    const startIso = new Date(form.start).toISOString();
    const endIso = new Date(form.end).toISOString();
    api.get('/training/trainer-availability', { params: { start: startIso, end: endIso } })
      .then(r => setTrainerAvail(r.data.trainers))
      .catch(() => {});
  }, [form.start, form.end]);

  if (!product) return null;
  const selectedClient = clients.find(c => c.id === form.client_id);
  const dogs = selectedClient?.dogs || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.dog_id || !form.trainer_id || !form.start) {
      toast.error('Please fill all required fields'); return;
    }
    if (isBnT && !form.run_id) { toast.error('Pick a run for Board & Train'); return; }
    setSubmitting(true);
    try {
      const payload = {
        client_id: form.client_id, dog_id: form.dog_id,
        program_type: product.program_type, trainer_id: form.trainer_id,
        run_id: isBnT ? form.run_id : null,
        start: new Date(form.start).toISOString(),
        end: new Date(form.end).toISOString(),
        behavioral_concerns: form.behavioral_concerns || null,
        bitten_history: form.bitten_history || null,
        medical_issues: form.medical_issues || null,
        notes: form.notes || null,
        payment_method: form.payment_method,
        origin_url: window.location.origin,
      };
      const { data } = await api.post('/training/bookings', payload);
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(`Training booked — ${data.program_label} for ${data.dog_name} with ${data.trainer_name}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!product} onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className='max-w-2xl max-h-[92vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <GraduationCap className='w-5 h-5 text-[#C8102E]' /> Schedule {product.name}
            <Badge className='bg-[#C8102E] text-slate-900'>${product.price.toFixed(0)}</Badge>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className='space-y-4'>
          <Card className='p-3 bg-slate-50 border-slate-200'>
            <div className='text-xs text-slate-600'>{product.description}</div>
          </Card>

          <div className='grid sm:grid-cols-2 gap-3'>
            <div>
              <Label>Client *</Label>
              <select required className='w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white' value={form.client_id} onChange={(e)=>setForm({...form, client_id: e.target.value, dog_id: ''})} data-testid='bnt-client-select'>
                <option value=''>— Pick a client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.full_name} ({c.email})</option>)}
              </select>
            </div>
            <div>
              <Label>Dog *</Label>
              <select required disabled={!form.client_id} className='w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white' value={form.dog_id} onChange={(e)=>setForm({...form, dog_id: e.target.value})} data-testid='bnt-dog-select'>
                <option value=''>— Pick a dog —</option>
                {dogs.map(d => <option key={d.id} value={d.id}>{d.name} {d.breed ? `(${d.breed})` : ''}</option>)}
              </select>
            </div>
          </div>

          <div className='grid sm:grid-cols-2 gap-3'>
            <div>
              <Label>Start * {duration > 0 && <span className='text-slate-500 text-xs'>(end auto-computed +{duration} days)</span>}</Label>
              <Input required type='datetime-local' value={form.start} onChange={(e)=>setForm({...form, start: e.target.value})} data-testid='bnt-start-input' />
            </div>
            <div>
              <Label>End</Label>
              <Input type='datetime-local' value={form.end} onChange={(e)=>setForm({...form, end: e.target.value})} data-testid='bnt-end-input' />
            </div>
          </div>

          <div>
            <Label>Trainer * {trainerAvail.length > 0 && <span className='text-slate-500 text-xs'>(availability shown for dates)</span>}</Label>
            <div className='grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1' data-testid='bnt-trainer-grid'>
              {(trainerAvail.length > 0 ? trainerAvail.map(ta => ({...ta.trainer, available: ta.available, conflict: ta.conflicts[0]})) : trainers.map(t=>({...t, available:true}))).map(t => (
                <button type='button' key={t.id} disabled={!t.available} onClick={()=>setForm({...form, trainer_id: t.id})}
                  className={`p-2 rounded-lg border text-left transition-all ${form.trainer_id === t.id ? 'border-[#1B3A87] bg-[#1B3A87]/10 ring-2 ring-[#1B3A87]/30' : t.available ? 'border-slate-200 hover:border-[#1B3A87]/40' : 'border-slate-200 bg-slate-100 opacity-60 cursor-not-allowed'}`}
                  data-testid={`bnt-trainer-${t.id}`}>
                  <div className='flex items-center gap-1.5'>
                    {t.avatar_url ? <img src={t.avatar_url} alt={t.full_name} className='w-7 h-7 rounded-full object-cover' /> : <div className='w-7 h-7 rounded-full bg-[#1B3A87] text-slate-900 flex items-center justify-center text-xs font-bold'>{t.full_name[0]}</div>}
                    <div className='min-w-0 flex-1'>
                      <div className='font-semibold text-xs truncate'>{t.full_name}</div>
                      <div className='text-[10px] text-slate-500 truncate'>{t.title || 'Trainer'}</div>
                    </div>
                    {t.available ? <CheckCircle2 className='w-4 h-4 text-[#167A5A] shrink-0' /> : <AlertTriangle className='w-4 h-4 text-[#B45309] shrink-0' />}
                  </div>
                  {!t.available && t.conflict && <div className='text-[9px] text-[#B45309] mt-1 truncate'>Busy: {t.conflict.dog_name}</div>}
                </button>
              ))}
            </div>
          </div>

          {isBnT && (
            <div>
              <Label>Run *</Label>
              <select required className='w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white' value={form.run_id} onChange={(e)=>setForm({...form, run_id: e.target.value})} data-testid='bnt-run-select'>
                <option value=''>— Pick a run —</option>
                {runs.map(r => <option key={r.id} value={r.id}>{r.name} ({r.type})</option>)}
              </select>
            </div>
          )}

          <details className='border border-slate-200 rounded-lg p-3'>
            <summary className='cursor-pointer text-sm font-semibold text-slate-700'>Intake details (from JFK9 form)</summary>
            <div className='mt-3 space-y-3'>
              <div><Label>Behavioral concerns</Label><Textarea rows={2} value={form.behavioral_concerns} onChange={(e)=>setForm({...form, behavioral_concerns: e.target.value})} data-testid='bnt-concerns' /></div>
              <div><Label>Has dog bitten in the past?</Label><Textarea rows={2} value={form.bitten_history} onChange={(e)=>setForm({...form, bitten_history: e.target.value})} data-testid='bnt-bitten' /></div>
              <div><Label>Medical issues / ailments</Label><Textarea rows={2} value={form.medical_issues} onChange={(e)=>setForm({...form, medical_issues: e.target.value})} data-testid='bnt-medical' /></div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} data-testid='bnt-notes' /></div>
            </div>
          </details>

          <div>
            <Label>Payment</Label>
            <div className='grid grid-cols-2 gap-2 mt-1'>
              {[{l:'Stripe',v:'stripe'},{l:'Cash / In-person',v:'cash'}].map(m => (
                <button type='button' key={m.v} onClick={()=>setForm({...form, payment_method: m.v})} className={`px-3 py-2 rounded border font-semibold text-sm ${form.payment_method === m.v ? 'border-[#1B3A87] bg-[#1B3A87] text-slate-900' : 'border-slate-300 bg-white'}`} data-testid={`bnt-pay-${m.v}`}>{m.l}</button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>Cancel</Button>
            <Button type='submit' disabled={submitting} className='bg-[#C8102E] hover:bg-[#A50D26] text-slate-900' data-testid='bnt-submit-button'>
              {submitting ? 'Scheduling…' : `Schedule — $${product.price.toFixed(0)}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
